import { useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaScript from '@/components/base/SchemaScript';
import { articles } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL;

const iconMap: Record<string, string> = {
  'steps': 'ri-numbers-line',
  'benefits': 'ri-star-line',
  'list': 'ri-checkbox-circle-line',
  'callout': 'ri-information-line',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleGoToCategories = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const el = document.getElementById('categories');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById('categories');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 400);
    }
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Return to Homepage</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.metaDescription,
    "author": { "@type": "Organization", "name": "itaobuy.bond" },
    "datePublished": article.date,
    "dateModified": article.date,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${siteUrl}/article/${article.slug}` }
  };

  return (
    <>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Article Header */}
          <section className="relative w-full pt-24 pb-10 md:pt-32 md:pb-14 bg-background-100">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-accent-100/40 via-background-100 to-primary-100/30"></div>
            </div>
            <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
              <div className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-500 transition-colors no-underline">
                  Home
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-400">{article.category}</span>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight mb-4 font-heading">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                {article.excerpt}
              </p>
              <div className="flex items-center justify-center gap-4 mt-6 text-xs text-foreground-500">
                <span>Updated: {article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </section>

          {/* Article Content */}
          <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="prose-custom text-foreground-800 leading-relaxed space-y-8">
              {article.sections.map((section) => (
                <section key={section.id}>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4 font-heading" id={section.id}>
                    <a href={`#${section.id}`} className="no-underline text-foreground-950 hover:text-primary-500 transition-colors">
                      {section.heading}
                    </a>
                  </h2>

                  {section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} className="text-sm md:text-base text-foreground-700 mb-4">
                      {p}
                    </p>
                  ))}

                  {/* Steps */}
                  {section.type === 'steps' && section.items && (
                    <div className="space-y-3 mb-4">
                      {section.items.map((item, iIdx) => (
                        <div key={iIdx} className="flex gap-4 p-4 rounded-lg bg-background-100 border border-background-200/70">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold flex-shrink-0">
                            {iIdx + 1}
                          </div>
                          <div>
                            <p className="text-sm text-foreground-600">{item}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Benefits */}
                  {section.type === 'benefits' && section.items && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {section.items.map((item, iIdx) => (
                        <div key={iIdx} className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                            <i className={`${iconMap[section.type || 'list']} text-lg text-accent-600`}></i>
                          </div>
                          <p className="text-xs text-foreground-600 leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* List */}
                  {section.type === 'list' && section.items && (
                    <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 my-4">
                      <ul className="space-y-2">
                        {section.items.map((item, iIdx) => (
                          <li key={iIdx} className="flex items-start gap-2 text-sm text-foreground-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0 mt-2"></span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Callout */}
                  {section.type === 'callout' && section.calloutText && (
                    <div className={`rounded-xl p-5 my-4 border ${
                      section.calloutType === 'info'
                        ? 'bg-accent-50 border-accent-200/70'
                        : 'bg-primary-50 border-primary-200/70'
                    }`}>
                      <h3 className="text-base font-semibold text-foreground-950 mb-2 font-heading flex items-center gap-2">
                        <i className={`${iconMap[section.type]} text-accent-600`}></i>
                        Important Note
                      </h3>
                      <p className="text-sm text-foreground-700 leading-relaxed">{section.calloutText}</p>
                    </div>
                  )}
                </section>
              ))}
            </div>

            {/* Internal Link Block */}
            <div className="mt-12 p-6 rounded-xl bg-background-100 border border-background-200/70">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4 font-heading">
                Explore More About ItaobuySpreadsheet
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                  to="/"
                  className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors p-3 rounded-lg hover:bg-background-50 no-underline"
                >
                  <i className="ri-home-line text-sm"></i>
                  ItaobuySpreadsheet Homepage
                </Link>
                <a
                  href="#categories"
                  onClick={handleGoToCategories}
                  className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors p-3 rounded-lg hover:bg-background-50 no-underline"
                >
                  <i className="ri-grid-line text-sm"></i>
                  Browse Product Categories
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors p-3 rounded-lg hover:bg-background-50 no-underline whitespace-nowrap"
                >
                  <i className="ri-external-link-line text-sm"></i>
                  Visit Itaobuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors p-3 rounded-lg hover:bg-background-50 no-underline whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line text-sm"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </article>

          {/* Back to Home CTA */}
          <section className="w-full px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-3xl mx-auto text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-all whitespace-nowrap no-underline"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>

      <SchemaScript data={articleSchema} />
    </>
  );
}